import { createSlice } from '@reduxjs/toolkit';
import { isNumber, reduce } from 'lodash';

const ssrInitialState = {
  select: 'tc-01',
  buttonStatus: 'on',
  switchName: null,
  description: [null, null, null],
  index: 1,
  isSettingOpen: false,
  openPasswordBox: false,
};

const initialState = {
  machineType: 'ess',
  deviceMac: null,
  deviceStatus: null,
  isEssSwitchActivated: false,
  displayConflictMessage: false,
  isEssSwitch: true,
  isSelected: false,

  isOff: false,
  freezeBy: null,
  isExpanded: false,

  openMachineController: false,
  isFaults: false,
  isGp: true,
  isEbp: false,
  atsSelection: [],
  isWifi: false,
  thermocouple: false,
  heaterThermocoupleMap: [],

  currentTemp: null,
  setTemp: null,
  consumption: null,
  enclosureTemp: null,
  outSideTemp: null,
  hoursOfUsage: null,

  instantHeat: { inputTemp: 0, isReady: false, isActivated: false },
  snowSensor: {
    isReady: false,
    isActivated: false,
    defaultTemp: 350,
  },

  optionalConstantTemp: {
    inputTemp: 0,
    apply: false,
    isActivated: false,
    isReady: false,
  },

  heatingScheduleList: [
    {
      start: { date: null, time: null },
      end: { date: null, time: null },
      inputTemp: null,
      id: null,
    },
  ],

  heatingSchedule: {
    isReady: false,
    isActivated: false,
    disable: false,
  },

  heatingSystem: '#10 Switch Turnout',
  heatingSystemAbbr: '',
  usageHours: 75,
  energyConsump: 300,
  reading: 30,
  address: { lat: 42.36997, lng: -71.070647 },

  windFactor: { isReady: false, isActivated: false },
  ats: { isActivated: false },
  mobileSelectedProgram: {
    instantHeat: false,
    snowSensor: false,
    windFactor: false,
    optionalConstantTemp: false,
    ats: false,
    shutOff: false,
    heatingSchedule: false,
  },

  ssrState: {
    ssr1: {
      ...ssrInitialState,
      specs: [],
    },
    ssr2: {
      ...ssrInitialState,
      currentCurrent: 0,
      specs: [],

      buttonStatus: 'flt',
    },
    ssr3: {
      ...ssrInitialState,
      specs: [],
    },
    ssr4: {
      ...ssrInitialState,
      specs: [],
      currentCurrent: [10.65],
    },
    ssr5: {
      ...ssrInitialState,
      specs: [],
    },
    ssr6: {
      ...ssrInitialState,
      specs: [],
    },
    ssr7: {
      ...ssrInitialState,
      specs: [],
      buttonStatus: 'off',
    },
    ssr8: {
      ...ssrInitialState,
      specs: [],
    },
  },

  heaterGraphData: [],
  enclosureGraphData: [],
  outsideGraphData: [],
  gasGraphData: [],
  snowGraphData: [],
  windGraphData: [],

  graphDateData: {
    heaterGraphData: [],
    enclosureGraphData: [],
    outsideGraphData: [],
    gasGraphData: [],
    snowGraphData: [],
    windGraphData: [],
    energyGasConsumptionGraphData: [],
    dataConsumptionGraphData: [],
    intervalUnit: '',
  },
};

const essSwitchSlice = createSlice({
  name: 'essSwitch',
  initialState: {
    'bet-east': {
      '01': initialState,
      '02': initialState,
      '03': initialState,
      '04': initialState,
      '05': initialState,
    },
    'bet-west': {
      '01': initialState,
      '02': initialState,
      '03': initialState,
      '04': initialState,
      '05': initialState,
    },
    's.coast': {
      '01': initialState,
      '02': initialState,
      '03': initialState,
      '04': initialState,
      '05': initialState,
    },
    'n-Mountain': {
      '01': initialState,
      '02': initialState,
      '03': initialState,
    },
    essSwitch: {},
    flatEssSwitch: {},
  },
  reducers: {
    handleSelectIndividualMachine: (state, action) => {
      const { location, machine } = action.payload;
      state.flatEssSwitch[location][machine].isSelected = true;
    },
    essSpecificLocationSelectMachinesHandler: (state, action) => {
      state.flatEssSwitch[action.payload.specificLocation][
        action.payload.machine
      ].isSelected = true;
    },
    handleUnSelectIndividualMachine: (state, action) => {
      const { location, machine } = action.payload;
      state.flatEssSwitch[location][machine].isSelected = false;
    },
    essSpecificLocationUnselectMachinesHandler: (state, action) => {
      state.flatEssSwitch[action.payload.specificLocation][
        action.payload.machine
      ].isSelected = false;
    },
    handleUnSelectAllIndividualMachines: (state) => {
      Object.values(state.flatEssSwitch).forEach((specificLocationObj) => {
        Object.values(specificLocationObj).forEach((machine) => {
          machine.isSelected = false;
        });
      });
    },
    essSpecificLocationUnselectAllMachinesHandler: (state) => {
      Object.values(state.flatEssSwitch).forEach((specificLocationObj) => {
        Object.values(specificLocationObj).forEach((machine) => {
          machine.isSelected = false;
        });
      });
    },

    handleInstantHeatReady: (state, action) => {
      const { location, machine, temp } = action.payload;
      state.flatEssSwitch[location][machine].instantHeat.inputTemp = temp;
      state.flatEssSwitch[location][machine].instantHeat.isReady = true;
    },
    handleInstantHeatOff: (state, action) => {
      state.flatEssSwitch[action.payload.location][
        action.payload.machine
      ].instantHeat.isActivated = false;

      state.flatEssSwitch[action.payload.location][
        action.payload.machine
      ].instantHeat.isReady = false;
    },
    handleInstantHeatReset: (state, action) => {
      state.flatEssSwitch[action.payload.location][
        action.payload.machine
      ].instantHeat = { ...initialState.instantHeat };
    },
    handleSnowSensor: (state, action) => {
      const { location, machine } = action.payload;
      state.flatEssSwitch[location][machine].snowSensor.isReady = true;

    },
    handleSnowSensorOff: (state, action) => {
      const { location, machine } = action.payload;
      state.flatEssSwitch[location][machine].snowSensor.isReady = false;
    },
    handleSnowSensorReset: (state, action) => {
      const { location, machine } = action.payload;

      state.flatEssSwitch[location][machine].snowSensor = {
        ...initialState.snowSensor,
      };
    },

    handleOptionalConstantTempReady: (state, action) => {
      const { location, machine, temp } = action.payload;
      state.flatEssSwitch[location][machine].optionalConstantTemp.inputTemp =
        temp;

      state.flatEssSwitch[location][machine].optionalConstantTemp.isReady =
        true;
    },

    handleMachineOptionalConstantTempOff: (state, action) => {
      const { location, machine } = action.payload;
      state.flatEssSwitch[location][machine].optionalConstantTemp.isActivated =
        false;
      state.flatEssSwitch[location][machine].optionalConstantTemp.isReady =
        false;
    },
    handleOptionalConstantTempReset: (state, action) => {
      const { location, machine } = action.payload;
      state.flatEssSwitch[location][machine].optionalConstantTemp = {
        ...initialState.optionalConstantTemp,
      };
    },
    handleWindFactor: (state, action) => {
      state.flatEssSwitch[action.payload.location][
        action.payload.machine
      ].windFactor.isReady = true;
    },
    handleWindFactorOff: (state, action) => {
      const { location, machine } = action.payload;
      state.flatEssSwitch[location][machine].windFactor.isReady = false;
    },
    handleWindFactorReset: (state, action) => {
      state.flatEssSwitch[action.payload.location][
        action.payload.machine
      ].windFactor = { ...initialState.windFactor };
    },
    handleAddHeatingSchedule: (state, action) => {
      const { location, machine, index, start, end, inputTemp, id } = action.payload;
      state.flatEssSwitch[location][machine].heatingScheduleList[index] = {
        start,
        end,
        inputTemp,
        id,
      };
      state.flatEssSwitch[location][machine].heatingScheduleList[index + 1] = {
        start: { date: null, time: null },
        end: { date: null, time: null },
        inputTemp: null,
        id: null,
      };
    },
    handleReadyHeatingSchedule: (state, action) => {
      const { location, machine } = action.payload;
      state.flatEssSwitch[location][machine].heatingSchedule.isReady =
        action.payload.state;
    },

    handleClearHeatingSchedule: (state, action) => {
      const { location, machine, data } = action.payload;
      state.flatEssSwitch[location][machine].heatingScheduleList = data;
    },
    handleHeatingScheduleReset: (state, action) => {
      const { location, machine } = action.payload;
      state.flatEssSwitch[location][machine].heatingScheduleList = {
        ...initialState.heatingScheduleList,
      };
      state.flatEssSwitch[location][machine].heatingSchedule = {
        ...initialState.heatingSchedule,
      };
    },

    handleShutOff: (state, action) => {
      const { location, machine } = action.payload;
      if (state.flatEssSwitch[location][machine].isOff) {
        state.flatEssSwitch[location][machine].isOff = false;
      } else {
        state.flatEssSwitch[location][machine].isOff = true;
      }
    },
    essResetMachinesState: (state, action) => {
      const { location, specificLocation, machine } = action.payload;

      const target = specificLocation
        ? state.flatEssSwitch[location]?.[specificLocation]?.[machine]
        : state.flatEssSwitch[location]?.[machine];

      if (!target) return;

      target.instantHeat = { ...initialState.instantHeat };
      target.optionalConstantTemp = { ...initialState.optionalConstantTemp };
      target.windFactor = { ...initialState.windFactor };
      target.heatingSchedule = { ...initialState.heatingSchedule };
      target.heatingScheduleList = initialState.heatingScheduleList.map(
        (schedule) => ({ ...schedule }),
      );
      target.snowSensor = {
        ...initialState.snowSensor,
        defaultTemp: target.snowSensor?.defaultTemp ?? null,
      };
    },

    handleSelector: (state, action) => {
      const { location, machine, id, data } = action.payload;
      state.flatEssSwitch[location][machine].ssrState[id].select = data;
    },
    handleToggleSSR: (state, action) => {
      const { location, machine, id, buttonStatus } = action.payload;
      state.flatEssSwitch[location][machine].ssrState[id].buttonStatus =
        buttonStatus;
    },
    handleExpandSSRDetail: (state, action) => {
      const { location, machine, status } = action.payload;
      state.flatEssSwitch[location][machine].isExpanded = status;
    },
    handleChangeSSRDetail: (state, action) => {
      const { location, machine, id, data } = action.payload;
      state.flatEssSwitch[location][machine].ssrState[id].specs = data;
    },

    handleOpenMachineController: (state, action) => {
      const { location, machine, status } = action.payload;
      state.flatEssSwitch[location][machine].openMachineController = status;
    },
    handleOpenSetting: (state, action) => {
      const { location, machine, id, status } = action.payload;
      state.flatEssSwitch[location][machine].ssrState[id].isSettingOpen =
        status;
    },

    handleOpenPasswordBox: (state, action) => {
      const { location, machine, id, status } = action.payload;
      state.flatEssSwitch[location][machine].ssrState[id].openPasswordBox =
        status;
    },

    handleAtsSelection: (state, action) => {
      const { location, machine, selection } = action.payload;
      state.flatEssSwitch[location][machine].atsSelection = selection;
    },

    essHandleUnselectAllProgram: (state, action) => {
      const { location, machine } = action.payload;
      const programsInitialState = {
        instantHeat: false,
        snowSensor: false,
        windFactor: false,
        optionalConstantTemp: false,
        ats: false,
        shutOff: false,
        heatingSchedule: false,
      };
      state.flatEssSwitch[location][machine].mobileSelectedProgram =
        programsInitialState;
    },
    essHandleSelectProgram: (state, action) => {
      const { location, machine, program } = action.payload;
      state.flatEssSwitch[location][machine].mobileSelectedProgram[program] =
        !state.flatEssSwitch[location][machine].mobileSelectedProgram[program];
    },
    handleEssSwitch: (state, action) => {
      delete state['bet-east'];
      delete state['bet-west'];
      delete state['n-Mountain'];
      delete state['s.coast'];

      const zones = action.payload.essZones;

      state.essSwitch = reduce(
        zones,
        (result, location) => {
          if (location.specific_location) {
            return {
              ...result,
              [location.zone_id]: {
                isSpecificLocation: true,
                subLocations: location.specific_location.reduce(
                  (result, specificLocation) => {
                    result[specificLocation.zone_id] = {
                      devices: reduce(
                        specificLocation.devices,
                        (result, machine) => {
                          return {
                            ...result,
                            [machine.device_mac]: {
                              ...initialState,
                              address: {
                                lat: location.latitude,
                                lng: location.longitude,
                              },
                              machineName: machine.device_name,
                              locationId: location.zone_id,
                              locationName: location.zone_name,
                              locationAddress: location.zone_address || '',
                              specificLocationName:
                                specificLocation.specific_address || '',
                              isOff: machine.freeze,
                              ...extractDataFromMachine(machine),
                            },
                          };
                        },
                        {},
                      ),
                    };
                    return result;
                  },
                  {},
                ),
              },
            };
          }
          return {
            ...result,
            [location.zone_id]: {
              isSpecificLocation: false,
              devices: reduce(
                location.devices,
                (result, machine) => {
                  return {
                    ...result,
                    [machine.device_mac]: {
                      ...initialState,
                      address: {
                        lat: location.latitude,
                        lng: location.longitude,
                      },
                      machineName: machine.device_name,
                      locationId: location.zone_id,
                      locationName: location.zone_name,
                      locationAddress: location.zone_address || '',
                      specificLocationName: location.specific_address || '',
                      isOff: machine.freeze,
                      ...extractDataFromMachine(machine),
                    },
                  };
                },
                {},
              ),
            },
          };
        },
        {},
      );
      state.flatEssSwitch = reduce(
        zones,
        (result, value) => {
          if (value.specific_location) {
            return {
              ...result,
              ...reduce(
                value.specific_location,
                (result, location) => ({
                  ...result,
                  [location.zone_id]: reduce(
                    location.devices,
                    (result, machine) => ({
                      ...result,
                      [machine.device_mac]: {
                        ...initialState,
                        address: {
                          lat: location.latitude,
                          lng: location.longitude,
                        },
                        machineName: machine.device_name,
                        deviceStatus: machine.device_status,
                        parentLocationId: value.zone_id,
                        locationId: location.zone_id,
                        locationName: location.zone_name,
                        locationAddress: location.zone_address || '',
                        specificLocationName: location.specific_address || '',
                        isOff: machine.freeze,
                        ...extractDataFromMachine(machine),
                      },
                    }),
                    {},
                  ),
                }),
                {},
              ),
            };
          }
          return {
            ...result,
            [value.zone_id]: reduce(
              value.devices,
              (result, machine) => ({
                ...result,
                [machine.device_mac]: {
                  ...initialState,
                  address: {
                    lat: value.latitude,
                    lng: value.longitude,
                  },
                  machineName: machine.device_name,
                  locationId: value.zone_id,
                  locationName: value.zone_name,
                  locationAddress: value.zone_address || '',
                  specificLocationName: value.specific_address || '',
                  isOff: machine.freeze,
                  ...extractDataFromMachine(machine),
                },
              }),
              {},
            ),
          };
        },
        {},
      );
    },
    handleEssSwitchSocket: (state, action) => {
      const data = action.payload.data;
      const location = data.zoneInfo.zone_id;
      const machine = data.device_mac;
      if (!state.flatEssSwitch[location]) {
        state.flatEssSwitch[location] = {};
      }

      if (!state.flatEssSwitch[location][machine]) {
        state.flatEssSwitch[location][machine] = {};
      }

      state.flatEssSwitch[location][machine] = {
        ...state.flatEssSwitch[location][machine],
        ...extractDataFromMachine(data),
      };
    },
    handleEssSSRState: (state, action) => {
      const { location, machine, data } = action.payload;
      state.flatEssSwitch[location][machine] = {
        ...state.flatEssSwitch[location][machine],
        ssrState: Object.fromEntries(
          Object.entries(
            reduce(
              data,
              (result, ssr) => {
                if (!isNumber(ssr.No)) {
                  return result;
                }
                return {
                  ...result,
                  [`ssr${ssr.No + 1}`]: {
                    ...state.flatEssSwitch[location][machine].ssrState[
                      `ssr${ssr.No + 1}`
                    ],
                    ...ssr,
                  },
                };
              },
              {},
            ),
          ).sort(([keyA], [keyB]) => keyA.localeCompare(keyB)),
        ),
      };
    },
    handleEssSSRStateSocket: (state, action) => {
      const data = action.payload;
      const location = data.zoneInfo.zone_id;
      const machine = data.device_mac || data.device_id;
      if (data.hasOwnProperty('specs')) {
        if (!isNumber(data.No)) {
          return;
        }
        state.flatEssSwitch[location][machine].ssrState[`ssr${data.No + 1}`] = {
          ...state.flatEssSwitch[location][machine].ssrState[
            `ssr${data.No + 1}`
          ],
          ...data,
          select: `tc-${
            state.flatEssSwitch[location][machine].heaterThermocoupleMap[
              data.No
            ]
          }`,
          buttonStatus:
            data?.fault === true || data?.Load_exceeded === true
              ? 'flt'
              : data?.active === true
                ? 'on'
                : 'off',
          switchName: `${data.name} ${data.size}`,
        };
        state.flatEssSwitch[location][machine].ssrFault[data.No] = data.fault
          ? 1
          : 0;
      } else {
        if (!Array.isArray(data.heater_thermocouple_map)) {
          return;
        }

        if (!state.flatEssSwitch[location]) {
          state.flatEssSwitch[location] = {};
        }

        if (!state.flatEssSwitch[location][machine]) {
          state.flatEssSwitch[location][machine] = {};
        }

        if (!state.flatEssSwitch[location][machine].ssrState) {
          state.flatEssSwitch[location][machine].ssrState = {};
        }

        data.heater_thermocouple_map.forEach((el, index) => {
          state.flatEssSwitch[location][machine].ssrState[`ssr${index + 1}`] = {
            ...state.flatEssSwitch[location][machine].ssrState[
              `ssr${index + 1}`
            ],
            select: `tc-${el}`,
          };
        });
      }
    },
    handleEssGraph: (state, action) => {
      const { location, machine, graphType, data } = action.payload;
      // Live graph events can arrive for a switch whose zone is not loaded.
      if (!state.flatEssSwitch[location]?.[machine]) {
        return;
      }
      state.flatEssSwitch[location][machine] = {
        ...state.flatEssSwitch[location][machine],
        [graphType]: data,
      };
    },
    handleEssGraphDate: (state, action) => {
      const { location, machine, graphType, data, unit } = action.payload;
      state.flatEssSwitch[location][machine].graphDateData = {
        ...state.flatEssSwitch[location][machine].graphDateData,
        [graphType]: data,
      };

      if (graphType !== 'dataConsumptionGraphData') {
        state.flatEssSwitch[location][machine].graphDateData.intervalUnit =
          unit;
      }
    },
  },
});

const extractDataFromMachine = (machine) => {
  const isMachineActive = machine.device_active === 1;

  return {
    ...machine,
    deviceMac: machine.device_mac,
    deviceStatus: machine.device_status,
    usageHours: isNumber(machine.hours_of_usage)
      ? Math.floor(machine.hours_of_usage / 3600)
      : null,
    reading: isNumber(machine.energy_reading)
      ? Math.floor(machine.energy_reading)
      : null,
    hoursOfUsage: isNumber(machine.hours_of_usage)
      ? Math.floor(machine.hours_of_usage / 3600)
      : null,
    enclosureTemp:
      isMachineActive && isNumber(machine.enclosure_temp)
        ? Math.floor(machine.enclosure_temp)
        : null,
    outSideTemp:
      isMachineActive && isNumber(machine.outside_temp)
        ? Math.floor(machine.outside_temp)
        : null,
    consumption:
      isMachineActive && isNumber(machine.energy_consumption)
        ? Math.round(machine.energy_consumption * 10) / 10
        : null,
    setTemp:
      isMachineActive && isNumber(machine.threshold_temp)
        ? Math.floor(machine.threshold_temp)
        : null,
    currentTemp:
      isMachineActive && isNumber(machine.display_temp)
        ? Math.floor(machine.display_temp)
        : null,
    isEbp: (machine.EBP === 1 || machine.EBP === true) && isMachineActive,
    isGp: (machine.EBP === 0 || machine.EBP === false) && isMachineActive,
    EBP_mode: machine.EBP_mode,
    instantHeat: {
      ...initialState.instantHeat,
      inputTemp: isNumber(machine.instant_temp) ? machine.instant_temp : null,
      isReady: isMachineActive && machine.on_switch === 1,
      isActivated:
        isMachineActive &&
        machine.on_switch === 1 &&
        machine.op_mode === 'SWITCH',
    },
    optionalConstantTemp: {
      ...initialState.optionalConstantTemp,
      inputTemp: isNumber(machine.constant_temp) ? machine.constant_temp : null,
      isReady: isMachineActive && machine.on_constant === 1,
      isActivated:
        isMachineActive &&
        machine.on_constant === 1 &&
        machine.op_mode === 'CONSTANT',
    },
    snowSensor: {
      ...initialState.snowSensor,
      defaultTemp: isNumber(machine.electrical_snow_threshold)
        ? Math.floor(machine.electrical_snow_threshold)
        : null,
      isReady: isMachineActive && machine.snow_enabled === 1,
      isActivated:
        isMachineActive &&
        machine.snow_enabled === 1 &&
        machine.snow_trigger === 1,
    },
    windFactor: {
      ...initialState.windFactor,
      isReady: isMachineActive && machine.wind_enabled === 1,
      isActivated:
        isMachineActive &&
        machine.wind_enabled === 1 &&
        machine.wind_trigger === 1,
    },
    heatingSchedule: {
      ...machine.heatingSchedule,
      isActivated: machine.schedule_enabled === 1,
    },
    isWifi: isMachineActive,
    isDisabled: !isMachineActive || machine.op_mode === 'FAULT',
    machineMode: machine.op_mode,
    thermocouple: machine.tc_mode === 1,
    heaterThermocoupleMap: machine.heater_thermocouple_map,
    activeThermocouples: machine.active_thermocouples,
    activatedHeaters: machine.activated_heaters,
    currentRun: machine.current_run,
    isFaults:
      isMachineActive &&
      ((Array.isArray(machine?.ssr_fault) && machine?.ssr_fault?.includes(1)) ||
        (Array.isArray(machine?.srr_over_current) &&
          machine.srr_over_current?.includes(1)) ||
        machine?.ground_fault === 1 ||
        (Array.isArray(machine?.thermocouple_fault) &&
          machine?.thermocouple_fault?.includes(1))),
    ssrFault: (isMachineActive && machine.ssr_fault) || [],
    srrOverCurrent: (isMachineActive && machine.srr_over_current) || [],
    groundFault: (isMachineActive && machine.ground_fault) || 0,
    thermocoupleFault: (isMachineActive && machine.thermocouple_fault) || [],
    switch_panels: machine.switch_panels,
    freezeBy: machine.freeze_by,
    ...(!isMachineActive && {
      heatingSchedule: {
        ...machine.heatingSchedule,
        isReady: false,
        isActivated: false,
      },
      heaterGraphData: [],
      enclosureGraphData: [],
      outsideGraphData: [],
      gasGraphData: [],
      snowGraphData: [],
      windGraphData: [],
    }),
  };
};

export default essSwitchSlice;

export const selectEssSwitch = (state) => state.essSwitch;
export const selectFlatEssSwitch = (state) => {
  return state.essSwitch;
};

export const {
  handleSelectIndividualMachine,
  essSpecificLocationSelectMachinesHandler,
  handleUnSelectIndividualMachine,
  essSpecificLocationUnselectMachinesHandler,

  handleInstantHeatOff,
  handleInstantHeatReady,
  handleInstantHeatReset,

  handleSnowSensorReset,
  handleSnowSensor,
  handleSnowSensorOff,
  handleSnowSensorIsActivatedOff,

  handleWindFactor,
  handleWindFactorOff,
  handleWindFactorReset,

  handleOptionalConstantTempReady,
  handleMachineOptionalConstantTempOff,
  handleOptionalConstantTempReset,

  handleShutOff,
  essResetMachinesState,

  handleAddHeatingSchedule,
  handleReadyHeatingSchedule,
  handleClearHeatingSchedule,
  handleHeatingScheduleReset,

  handleSelector,
  handleToggleSSR,
  handleExpandSSRDetail,
  handleChangeSSRDetail,
  handleOpenSetting,
  handleOpenPasswordBox,

  handleOpenMachineController,
  handleAtsSelection,

  essHandleUnselectAllProgram,
  essHandleSelectProgram,

  handleHeatingSchedule,

  handleEssSwitch,
  handleEssSwitchSocket,
  handleEssSSRState,
  handleEssSSRStateSocket,
  handleEssGraph,
  handleEssGraphDate,
  handleUnSelectAllIndividualMachines,
  essSpecificLocationUnselectAllMachinesHandler,
} = essSwitchSlice.actions;
